/**
 * Unread message conversation count for Messages badges.
 * Message push rows live separately from the Notifications route and are read
 * when the corresponding DM, group, or club thread is opened.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/auth.reducer';
import { getUnreadMessageConversationCount } from '../helpers/messagesHelpers';
import { useSocket } from './SocketContext';

const MessageCountContext = createContext({
  unreadMessageCount: 0,
  refreshUnreadMessageCount: async () => {},
});

export function MessageCountProvider({ children }) {
  const user = useSelector(selectUser);
  const userId = user?.user_id ?? null;
  const { subscribe, connected } = useSocket();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const refreshUnreadMessageCount = useCallback(async () => {
    if (!userId) {
      setUnreadMessageCount(0);
      return;
    }
    try {
      const count = await getUnreadMessageConversationCount();
      setUnreadMessageCount(typeof count === 'number' ? count : 0);
    } catch {
      /* ignore */
    }
  }, [userId]);

  useEffect(() => {
    refreshUnreadMessageCount();
  }, [refreshUnreadMessageCount]);

  useEffect(() => {
    const onRead = () => refreshUnreadMessageCount();
    window.addEventListener('messages:read', onRead);
    return () => window.removeEventListener('messages:read', onRead);
  }, [refreshUnreadMessageCount]);

  useEffect(() => {
    if (!connected || !userId) return undefined;
    const unsubscribers = [
      subscribe('new_message', refreshUnreadMessageCount),
      subscribe('new_group_message', refreshUnreadMessageCount),
      subscribe('club_chat_message', refreshUnreadMessageCount),
    ];
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [connected, userId, subscribe, refreshUnreadMessageCount]);

  return (
    <MessageCountContext.Provider value={{ unreadMessageCount, refreshUnreadMessageCount }}>
      {children}
    </MessageCountContext.Provider>
  );
}

export function useMessageCount() {
  return useContext(MessageCountContext);
}
